/*!
// <copyright file="Authentication.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util, appletMessaging } from "@ax/office-shared-lib";
import { appState } from "./AppState";
import { resources } from "@ax/office-cdn-app-resources";
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { notificationHandling } from "./NotificationHandling";
import * as msal from "@azure/msal-browser";
import { AuthenticatedCallBack, Authenticator, VoidNoArgCallback } from "./Authenticator";
import { DialogMode, DialogMessageType, DialogAuthConfig, DialogSignInConfig, DialogSignOutConfig } from "./AuthenticationDialogConfig";
import type { DialogMessage } from "./AuthenticationDialogConfig";

export namespace authentication {

    /** The regular expression used to extract authentication error codes. */
    export const AuthErrorCodeRegex = /(AADSTS[0-9]*)/;

    /** The regular expression used to match the app page. */
    export const AppPageRegex = /\/App\/([a-zA-Z]+\/)?[a-zA-Z]+\.html.*/;

    /** The regular expression used to check if there is a forward slash at the end of the authentication realm in the server configuration */
    export const EndsWithForwardSlashRegex = /\/$/;

    /** The mechanisms to facilitate OAuth login. */
    /**
     * Extracts the error code from the provided authentication error message.
     * @param errorMessage The authentication error message.
     * @returns The extracted error code or a generic error message if no error code could be found.
     */
    export function extractAuthErrorCode(errorMessage: string): string {
        var errorCode = resources.appletInteraction.authenticationError();
        var errorMessageMatches = errorMessage ? errorMessage.match(authentication.AuthErrorCodeRegex) : <string[]>[];
        if (errorMessageMatches && errorMessageMatches.length > 0) {
            errorCode = errorMessageMatches[0];
        }
        return errorCode;
    }

    /**
     * Converts a MSAL error to a string.
     * @param error MSAL error to build a string from.
     * @returns Input MSAL error as a string or a generic error message if the error was undefined/null.
     */
    export function authErrorToString(error: msal.AuthError): string {
        var ret: string = resources.appletInteraction.authenticationError();
        if (error) {
            if (error.stack) {
                ret = error.stack;
            } else if (error.message) {
                ret = error.message;
            } else {
                ret = error.errorCode + ": " + error.errorMessage;
            }
        }

        return ret;
    }

    const redirectUri = window.location.href.replace(window.location.search, "").replace(window.location.hash, "");

    /** Provides a common mechanism for verifying authentication and prompting the user for authentication via MSAL OAuth. */
    export class MSALOAuthAuthenticator implements Authenticator {

        /** Indicates if the authenticator is already authenticated. */
        public isAuthenticated: boolean;

        /** Indicates if the authenticator is currently authenticating. */
        public isAuthenticating: boolean;

        /** The callback to call while authenticating. */
        private authenticatingCallback: VoidNoArgCallback;

        /** The callback to call if it is determined the user is NOT authenticated. */
        private notAuthenticatedCallback: VoidNoArgCallback;

        /** The callback to call once the sign in window has been opened. */
        private signInCallback: VoidNoArgCallback;

        /** The callback to call if it is determined the user is authenticated. */
        private authenticatedCallback: AuthenticatedCallBack;

        /** The configuration object for the MSAL JS library. */
        private msalConfig: msal.Configuration;

        /** Cached access token received from the auth dialog (used when storage is partitioned). */
        private dialogAccessToken: string;

        /** Expiry time (ms since epoch) of the cached dialog access token. */
        private dialogTokenExpiresOn: number;

        /** Scopes the cached dialog access token was issued for. */
        private dialogTokenScopes: string[];

        /** Cached account received from the auth dialog (used when storage is partitioned). */
        private dialogAccount: msal.AccountInfo;

        /** The state of the application being authenticated. */
        private state: appState.ApplicationState<appState.AddinSettings>;

        /** The error code returned when the authentication dialog is manually closed. */
        private static DialogClosedErrorCode = 12006;
        
        /** Buffer (in ms) subtracted from token expiry to ensure tokens are refreshed before they expire. */
        private static TokenExpiryBufferMs = 5 * 60 * 1000;

        /**
         * @param authenticatingCallback The callback to call while authenticating.
         * @param notAuthenticatedCallback The callback to call if it is determined the user is NOT authenticated.
         * @param signInCallback The callback to call once the sign in window has been opened.
         * @param authenticatedCallback The callback to call if it is determined the user is authenticated.
         * @param state The state of the application being authenticated.
         */
        constructor(
            authenticatingCallback: VoidNoArgCallback,
            notAuthenticatedCallback: VoidNoArgCallback,
            signInCallback: VoidNoArgCallback,
            authenticatedCallback: AuthenticatedCallBack,
            state: appState.ApplicationState<appState.AddinSettings>) {

            this.authenticatingCallback = authenticatingCallback;
            this.notAuthenticatedCallback = notAuthenticatedCallback;
            this.signInCallback = signInCallback;
            this.authenticatedCallback = authenticatedCallback;

            this.isAuthenticated = false;
            this.isAuthenticating = false;
            this.isValid = this.isValid.bind(this);
            this.requestSignIn = this.requestSignIn.bind(this);
            this.signOut = this.signOut.bind(this);
            this.state = state;
            this.msalConfig = {
                auth: {
                    clientId: null,
                    authority: "https://login.microsoftonline.com/common",
                    redirectUri: redirectUri,
                    postLogoutRedirectUri: redirectUri
                },
                cache: {
                    cacheLocation: "localStorage"
                },
                system: {
                    loggerOptions: {
                        loggerCallback: (level: msal.LogLevel, message: string, containsPii: boolean): void => {
                            telemetry.events.oauthLogEvent.log(message);
                        },
                        piiLoggingEnabled: false
                    }
                }
            };

            if (!state.ServerConfig.AuthRealm) {
                telemetry.errors.noAuthenticationRealm.show(resources.authentication.invalidServerConfiguration(), { Details: resources.authentication.noAuthRealm() });
                return;
            } else if (!state.ServerConfig.AuthClientId) {
                telemetry.errors.noClientID.show(resources.authentication.invalidServerConfiguration(), { Details: resources.authentication.noAuthClientId() });
                return;
            } else if (!state.ServerConfig.AuthResourceId) {
                telemetry.errors.noAuthenticationResourceID.show(resources.authentication.invalidServerConfiguration(), { Details: resources.authentication.noAuthResourceId() });
                return;
            }

            // If the authentication realm in the server configuration does not end with a forward slash, add it.
            if (!authentication.EndsWithForwardSlashRegex.test(state.ServerConfig.AuthRealm)) {
                state.ServerConfig.AuthRealm = state.ServerConfig.AuthRealm.concat("/");
            }

            // If the authentication resource ID ends with a slash, remove it
            if (authentication.EndsWithForwardSlashRegex.test(state.ServerConfig.AuthResourceId)) {
                state.ServerConfig.AuthResourceId = state.ServerConfig.AuthResourceId.replace(/\/+$/, "");
            }

            // All authentication mechanisms require the resource ID to properly determine
            // authentication, and redirect for cases where user interaction is required.
            localStorage.msalResourceID = state.ServerConfig.AuthResourceId;

            telemetry.events.oauthLoginMechanismDetermined.log("Dialog");

            // If a tenant is not provided in the server configuration, fallback to 'common'
            if (state.ServerConfig.AuthTenantId) {
                localStorage.msalConfigAuthority = this.msalConfig.auth.authority = state.ServerConfig.AuthRealm + state.ServerConfig.AuthTenantId;
                if (this.msalConfig.auth.authority.indexOf("/adfs") !== -1) {
                    if (!this.msalConfig.auth.knownAuthorities) {
                        this.msalConfig.auth.knownAuthorities = [];
                    }
                    this.msalConfig.auth.knownAuthorities.push(new URL(this.msalConfig.auth.authority).hostname);
                    this.msalConfig.system.protocolMode = msal.ProtocolMode.OIDC;
                }
            } else {
                localStorage.msalConfigAuthority = this.msalConfig.auth.authority = state.ServerConfig.AuthRealm + "common";
            }

            localStorage.msalConfigClientID  = this.msalConfig.auth.clientId = state.ServerConfig.AuthClientId;

            try {
                localStorage.msalTokenScopes = JSON.stringify(this.state.getMsalTokenScopes());
                localStorage.msalLoginScopes = JSON.stringify(this.state.getMsalLoginScopes());
            } catch (error) {}

            this.checkAuthentication();
        }

        /**
         * Updates the callbacks used by the authenticator.
         * @param authenticatingCallback The callback to call while authenticating.
         * @param notAuthenticatedCallback The callback to call if it is determined the user is NOT authenticated.
         * @param signInCallback The callback to call once the sign in window has been opened.
         * @param authenticatedCallback The callback to call if it is determined the user is authenticated.
         */
        public updateCallbacks(authenticatingCallback: VoidNoArgCallback, notAuthenticatedCallback: VoidNoArgCallback, signInCallback: VoidNoArgCallback, authenticatedCallback: AuthenticatedCallBack): void {
            this.authenticatingCallback = authenticatingCallback;
            this.notAuthenticatedCallback = notAuthenticatedCallback;
            this.signInCallback = signInCallback;
            this.authenticatedCallback = authenticatedCallback;
        }

        /**
         * Determine whether the authenticator is valid.
         * @returns A value indicating whether the authenticator is valid.
         */
        public isValid(): boolean {
            return !!this.msalConfig;
        }

        /** Called when the user has clicked the "sign in" link. Triggers the OAuth login process. */
        public requestSignIn(): void {

            if (this.isValid()) {
                telemetry.events.oAuthSignInEvent.log();

                this.signInCallback();

                // Rejection is handled inside openRedirectAuthenticationDialog (telemetry + notAuthenticated).
                this.openRedirectAuthenticationDialog().catch(() => {});
            } else {
                telemetry.errors.oAuthAuthenticationFailed.show(resources.appletInteraction.authenticationError(), "", "",);
                this.notAuthenticated();
            }
        }

        /**
         * Gets the active account.
         * @returns An object representing the active account.
         */
        public getActiveAccount(): msal.AccountInfo {
            return this.dialogAccount;
        }

        /** Whether the authority uses ADFS (OIDC protocol mode), which only provides tokens with the 'openid' scope. */
        private isAdfs(): boolean {
            return this.msalConfig.system?.protocolMode === msal.ProtocolMode.OIDC;
        }

        /**
         * Opens the AuthenticationDialog and wires up the dialogReady/messageChild handshake,
         * event logging, and error handling. Callers provide the config and outcome callbacks.
         */
        private openDialog(
            config: DialogAuthConfig,
            handlers: {
                onSuccess: (dialog: Office.Dialog, messageObject: DialogMessage) => void,
                onDialogEvent: (dialog: Office.Dialog, message: { error: number }) => void,
                onOpenError: () => void
            }
        ): void {
            Office.context.ui.displayDialogAsync(
                window.location.href.replace(authentication.AppPageRegex, "/App/AuthenticationDialog.html"),
                <any>{ height: 75, width: 25, xFrameDenySafe: false, displayInIframe: false, enforceAppDomains: false, requireHTTPS: false },
                (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const dialog: Office.Dialog = asyncResult.value;

                        dialog.addEventHandler(
                            Office.EventType.DialogMessageReceived,
                            (message: { message: string, origin: string | undefined }) => {
                                let messageObject: DialogMessage;
                                try { messageObject = JSON.parse(message.message); } catch (e) { }

                                if (messageObject?.messageType === DialogMessageType.DialogReady) {
                                    (dialog as any).messageChild(JSON.stringify(config));
                                    return;
                                }

                                if (messageObject?.messageType === DialogMessageType.OAuthEvent) {
                                    telemetry.events.oauthLogEvent.log(messageObject.event);
                                    return;
                                }

                                handlers.onSuccess(dialog, messageObject);
                            });
                        dialog.addEventHandler(
                            Office.EventType.DialogEventReceived,
                            (message: { error: number }) => {
                                handlers.onDialogEvent(dialog, message);
                            });
                    } else {
                        appletMessaging.createCloneableDeepCopy(
                            asyncResult.error,
                            (errorCopy) => {
                                telemetry.errors.oAuthDialogOpenError.add(resources.authentication.authDialogError(), JSON.stringify(errorCopy));
                                handlers.onOpenError();
                            });
                    }
                });
        }

        /**
         * Performs login through a custom dialog to get around iframe sandboxing restrictions.
         * @returns A promise fulfilled after dialog-based authentication completes.
         */
        private openRedirectAuthenticationDialog(): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                let tokenScopes: string;
                let loginScopes: string;
                try {
                    tokenScopes = JSON.stringify(this.state.getMsalTokenScopes());
                    loginScopes = JSON.stringify(this.state.getMsalLoginScopes());
                } catch (error) {
                    tokenScopes = "";
                    loginScopes = "";
                }

                const dialogConfig: DialogSignInConfig = {
                    mode: DialogMode.SignIn,
                    msalConfigClientID: this.msalConfig.auth.clientId,
                    msalConfigAuthority: this.msalConfig.auth.authority,
                    msalLoginScopes: loginScopes,
                    msalTokenScopes: tokenScopes,
                    msalResourceID: this.state.ServerConfig.AuthResourceId,
                    oauthInitialLogin: "true",
                    signInDescription: resources.authentication.signInAsFormat(this.state.Settings.HostName),
                    signInButtonLabel: resources.dynamicsApp.signInButtonLabel(),
                    officeAppThemePrefix: this.state.CurrentApplication === util.ApplicationType.Word ? "Word" : "Excel"
                };

                this.openDialog(dialogConfig, {
                    onSuccess: (dialog, messageObject) => {
                        dialog.close();

                        if (messageObject?.messageType === DialogMessageType.Authenticated) {
                            this.dialogAccessToken = messageObject.accessToken;
                            this.dialogTokenExpiresOn = messageObject.expiresOn || (Date.now() + 3600 * 1000);
                            this.dialogTokenScopes = messageObject.scopes || [];
                            this.dialogAccount = messageObject.account;
                            this.authenticated();
                            resolve();
                            return;
                        }

                        if (messageObject?.messageType === DialogMessageType.OAuthError) {
                            telemetry.errors.authDialogLoginError.show(
                                messageObject.error.errorMessage,
                                authentication.extractAuthErrorCode(messageObject.error.errorMessage),
                                {
                                    CopyText: messageObject.error.errorMessage
                                });
                        }

                        this.notAuthenticated();
                        reject(messageObject);
                    },
                    onDialogEvent: (dialog, message) => {
                        if (message.error !== MSALOAuthAuthenticator.DialogClosedErrorCode) {
                            telemetry.errors.oAuthDialogError.show(
                                resources.authentication.authDialogError(),
                                message.error,
                                {
                                    LinkOptions: { Text: resources.authentication.signOutLabel(), Action: this.signOut }
                                });
                            dialog.close();
                        } else {
                            // dialog.close() after successful auth fires this event with
                            // DialogClosedErrorCode - ignore it if we already authenticated.
                            if (this.isAuthenticated) {
                                return;
                            }
                        }

                        this.notAuthenticated();
                        reject(message);
                    },
                    onOpenError: () => {
                        this.notAuthenticated();
                        reject();
                    }
                });
            });
        }

        /** Determines if the user is currently signed in. */
        public checkAuthentication(): void {
            if (this.isValid()) {
                if (!this.isAuthenticated && !this.isAuthenticating) {
                    this.authenticating();

                    const account: msal.AccountInfo = this.getActiveAccount();
                    if (account 
                        && this.dialogAccessToken 
                        && this.dialogTokenExpiresOn > (Date.now() + MSALOAuthAuthenticator.TokenExpiryBufferMs)) {
                        this.authenticated();
                    } else {
                        this.notAuthenticated();
                    }
                }
            } else {
                this.notAuthenticated();
            }
        }

        /** Called when authentication has completed successfully. */
        private authenticated(): void {
            let originSet: util.PropertyBag<string> = {};
            const hostOrigin = util.extractHostName(this.state.Settings.HostName);
            originSet[hostOrigin] = hostOrigin;
            this.state.AuthContext = this;
            const account = this.getActiveAccount();
            notificationHandling.addNotification(resources.authentication.loggedInAs(account?.username));
            this.isAuthenticated = true;
            this.isAuthenticating = false;
            this.authenticatedCallback(originSet, account);
        }

        /** Called when authentication is starting. */
        private authenticating() {
            this.isAuthenticating = true;
            this.authenticatingCallback();
        }

        /** Called when authentication has completed, but the user is not authenticated successfully. */
        private notAuthenticated() {
            this.isAuthenticating = false;
            this.notAuthenticatedCallback();
        }

        /** Signs out the current user. */
        public signOut(): void {
            if (this.isValid()) {
                telemetry.events.oAuthSignOutEvent.log();

                const signingOutLabel = resources.dynamicsApp.status_BusyFormattedMessage(resources.authentication.signingOutMessage());

                telemetry.operations.signingOut.begin(resources.authentication.signingOutMessage());

                const signOutConfig: DialogSignOutConfig = {
                    mode: DialogMode.SignOut,
                    msalConfigClientID: this.msalConfig.auth.clientId,
                    msalConfigAuthority: this.msalConfig.auth.authority,
                    signingOutLabel: signingOutLabel
                };

                const completeSignOut = (message: string) => {
                    // Clear authentication state
                    this.dialogAccessToken = undefined;
                    this.dialogTokenExpiresOn = 0;
                    this.dialogTokenScopes = [];
                    this.dialogAccount = undefined;
                    this.isAuthenticated = false;

                    // Message the user and trigger reload
                    telemetry.operations.signingOut.end(message);
                    this.reloadAddin();
                };

                this.openDialog(signOutConfig, {
                    onSuccess: (dialog) => {
                        dialog.close();
                        completeSignOut(resources.authentication.signedOutMessage());
                    },
                    onDialogEvent: (dialog, message) => {
                        if (message.error !== MSALOAuthAuthenticator.DialogClosedErrorCode) {
                            telemetry.errors.oAuthSignoutDialogError.add(resources.authentication.signOutFailedMessage(), message.error);
                            dialog.close();
                        }
                        completeSignOut(resources.authentication.signOutFailedMessage());
                    },
                    onOpenError: () => {
                        completeSignOut(resources.authentication.signOutFailedMessage());
                    }
                });
            }
        }

        /**
         * Acquires a token for a given resource.
         * @param resourceId The ID of the resource for which to acquire a token.
         * @returns A promise fulfilled after silent token acquisition has completed.
         */
        public acquireToken(resourceId: string): Promise<string> {
            return new Promise<string>((resolve, reject) => {
                if (this.isValid()) {
                    const requestedScope = resourceId + "/.default";
                    const scopeMatches = this.isAdfs()
                        || this.dialogTokenScopes?.some(s => s.toLowerCase() === requestedScope.toLowerCase());
                    if (this.dialogAccessToken
                        && this.dialogTokenExpiresOn > (Date.now() + MSALOAuthAuthenticator.TokenExpiryBufferMs)
                        && scopeMatches) {
                        resolve(this.dialogAccessToken);
                        return;
                    }
                    this.dialogAccessToken = undefined;
                    this.dialogTokenExpiresOn = 0;

                    this.openRedirectAuthenticationDialog().then(() => {
                        if (this.dialogAccessToken) {
                            resolve(this.dialogAccessToken);
                        } else {
                            reject(new msal.AuthError("no_token", resources.appletInteraction.authenticationError()));
                        }
                    }, (error) => {
                        reject(error);
                    });
                } else {
                    reject(new msal.AuthError("error", resources.appletInteraction.authenticationError()));
                }
            });
        }

        /**
         * Reloads the add-in by reloading the add-in page.
         */
        private reloadAddin(): void {
            window.location.reload();
        }
    }

    /** The authenticator used by the test framework to authenticate automated tests. */
    export class TestFrameworkAuthenticator implements Authenticator {

        /** The user name. */
        public static userName: string;

        /** The user's display name. */
        public static displayName: string;

        /** The user's token. */
        public static token: string;

        /**
         * @param authenticatingCallback The callback to call while authenticating.
         * @param notAuthenticatedCallback The callback to call if it is determined the user is NOT authenticated.
         * @param signInCallback The callback to call once the sign in window has been opened.
         * @param authenticatedCallback The callback to call if it is determined the user is authenticated.
         * @param state The state of the application being authenticated.
         */
        constructor(
            public authenticatingCallback: VoidNoArgCallback,
            public notAuthenticatedCallback: VoidNoArgCallback,
            public signInCallback: VoidNoArgCallback,
            public authenticatedCallback: AuthenticatedCallBack,
            public state: appState.ApplicationState<appState.AddinSettings>) {
        }

        /**
         * Updates the callbacks used by the authenticator.
         * @param authenticatingCallback The callback to call while authenticating.
         * @param notAuthenticatedCallback The callback to call if it is determined the user is NOT authenticated.
         * @param signInCallback The callback to call once the sign in window has been opened.
         * @param authenticatedCallback The callback to call if it is determined the user is authenticated.
         */
        public updateCallbacks(authenticatingCallback: VoidNoArgCallback, notAuthenticatedCallback: VoidNoArgCallback, signInCallback: VoidNoArgCallback, authenticatedCallback: AuthenticatedCallBack): void {
            this.authenticatingCallback = authenticatingCallback;
            this.notAuthenticatedCallback = notAuthenticatedCallback;
            this.signInCallback = signInCallback;
            this.authenticatedCallback = authenticatedCallback;
        }

        /** Called when the user has clicked the "sign in" link. */
        public requestSignIn(): void {
            throw new Error("Not supported");
        }

        /**
         * Determines if the user is currently signed in.
         * @param forceRefresh Optional parameter indicating if an authentication refresh should be forced, defaulting to false.
         */
        public checkAuthentication(forceRefresh?: boolean): void {
            var originSet: util.PropertyBag<string> = {};
            var hostOrigin = util.extractHostName(this.state.Settings.HostName);
            originSet[hostOrigin] = hostOrigin;
            const user: msal.AccountInfo = {
                name: TestFrameworkAuthenticator.displayName,
                tenantId: null,
                localAccountId: null,
                homeAccountId: null,
                environment: null,
                username: TestFrameworkAuthenticator.userName
            }
            this.state.AuthContext = {
                acquireToken: this.acquireToken,
                getCachedUser: () => {
                    return user;
                }
            } as any;
            this.isAuthenticated = true;
            this.authenticatedCallback(originSet, user);
        }

        /** Signs out the current user. */
        public signOut(): void {
            throw new Error("Not supported");
        }

        /** Indicates if the authenticator is already authenticated. */
        isAuthenticated: boolean;

        /**
         * Determine whether the authenticator is valid.
         * @returns A value indicating whether the authenticator is valid.
         */
        public isValid(): boolean {
            return true;
        }

        /**
         * Acquires a token for a given resource.
         * @param resourceId The ID of the resource for which to acquire a token.
         * @param callback The callback performed once a token has been acquired, or fails to acquire.
         */
        public acquireToken(resourceId: string): Promise<string> {
            return Promise.resolve(TestFrameworkAuthenticator.token);
        }

        /**
         * Gets the active account.
         * @returns An object representing the active account.
         */
         public getActiveAccount(): msal.AccountInfo {
            const user: msal.AccountInfo = {
                name: TestFrameworkAuthenticator.displayName,
                tenantId: null,
                localAccountId: null,
                homeAccountId: null,
                environment: null,
                username: TestFrameworkAuthenticator.userName
            }

            return user;
        }
    }

    /** The authenticator that's currently being used. */
    let currentAuthenticator: Authenticator;

    /** The cache of current authenticators - keyed by a key unique to an authentication configuration. */
    const cachedAuthenticators: Record<string, Authenticator> = {};

    /** Represents authentication configuration. */
    export interface AuthenticationConfiguration {

        /** The callback to call while authenticating. */
        authenticatingCallback: VoidNoArgCallback;

        /** The callback to call if it is determined the user is NOT authenticated. */
        notAuthenticatedCallback: VoidNoArgCallback;

        /** The callback to call once the sign in window has been opened. */
        signInCallback: VoidNoArgCallback;

        /** The callback to call if it is determined the user is authenticated. */
        authenticatedCallback: AuthenticatedCallBack;

        /** The state of the application being authenticated. */
        state?: appState.ApplicationState<appState.AddinSettings>;
    }

    /**
     * Provides a common mechanism for verifying authentication and prompting the user for authentication.
     * @param authenticatingCallback The callback to call while authenticating.
     * @returns An instance of the authenticator corresponding to the configuration, or the current (last created) authenticator if no configuration is provided.
     */
    export function getAuthenticator(configuration?: AuthenticationConfiguration): Authenticator {
        if (configuration) {

            configuration.state = configuration.state || appState.State;

            const serverConfig = configuration.state?.ServerConfig;
            const configurationMoniker = serverConfig ? `${serverConfig.AuthType}_${serverConfig.AuthClientId}_${serverConfig.AuthRealm}_${serverConfig.AuthTenantId}_${serverConfig.AuthResourceId}` : null;

            // Log an event that indicates what type of authentication is used. This will help us determine if / when we can remove cookie authentication support.
            telemetry.events.authTypeDetermined.log(configuration.state.ServerConfig.AuthType);

            if (configurationMoniker && configurationMoniker in cachedAuthenticators) {
                currentAuthenticator = cachedAuthenticators[configurationMoniker];
                currentAuthenticator.updateCallbacks(
                    configuration.authenticatingCallback,
                    configuration.notAuthenticatedCallback,
                    configuration.signInCallback,
                    configuration.authenticatedCallback)
            } else {
                if (configuration.state.ServerConfig.AuthType === appState.AuthType.OAuth) {
                    currentAuthenticator = new authentication.MSALOAuthAuthenticator(
                        configuration.authenticatingCallback,
                        configuration.notAuthenticatedCallback,
                        configuration.signInCallback,
                        configuration.authenticatedCallback,
                        configuration.state);
                } else {
                    telemetry.errors.cookieAuthenticationFailed.show(
                        resources.authentication.invalidServerConfiguration(),
                        { Details: "Cookie authentication is no longer supported." });
                    return currentAuthenticator;
                }

                if (configurationMoniker) {
                    cachedAuthenticators[configurationMoniker] = currentAuthenticator;
                }
            }
        }
        return currentAuthenticator;
    }

    /** The test authenticator that's currently being used. */
    let currentTestAuthenticator: Authenticator;

    /**
     * Provides a common mechanism for verifying test authentication.
     * @param configuration The authentication configuration.
     * @returns An instance of the test authenticator corresponding to the configuration, or the current (last created) test authenticator if no configuration is provided.
     */
    export function getTestAuthenticator(configuration?: AuthenticationConfiguration): Authenticator {
        if (configuration) {
            configuration.state = configuration.state || appState.State;
            currentTestAuthenticator = new authentication.TestFrameworkAuthenticator(
                configuration.authenticatingCallback,
                configuration.notAuthenticatedCallback,
                configuration.signInCallback,
                configuration.authenticatedCallback,
                configuration.state);
        }
        return currentTestAuthenticator;
    }
}
